<script setup lang="ts">
import { ref, watch } from 'vue'
import { NModal, NInput, NButton, NAlert, NText } from 'naive-ui'
import { apiMutate } from '../composables/apiMutate'
import { useRestoreFocusOnClose } from '../composables/useRestoreFocusOnClose'

// Admin-only "Dispatch task" surface. Fires the pre-configured
// Claude Code routine via POST /api/v1/dispatch_tasks and links the live
// cloud session it returns. The routine itself (repo / base prompt /
// environment / permissions) is baked in on claude.ai; the only thing
// dispatchable here is an optional freeform context blob, so the form is
// a single optional textarea.

const show = defineModel<boolean>('show', { required: true })

// Return focus to the trigger button when the modal closes.
useRestoreFocusOnClose(show)

interface DispatchResult {
  session_id: string | null
  session_url: string
}

const text = ref('')
const submitting = ref(false)
const error = ref<string | null>(null)
const result = ref<DispatchResult | null>(null)
const inputRef = ref<InstanceType<typeof NInput> | null>(null)

// Reset every time the modal opens so a prior run's result/error never
// bleeds into a fresh dispatch, and land focus on the textarea so a
// keyboard user can type immediately. The focus is deferred a
// frame so NModal's own focus trap settles first — a plain nextTick
// loses the textarea to the modal's focus sentinel.
watch(show, (open) => {
  if (!open) return
  text.value = ''
  error.value = null
  result.value = null
  submitting.value = false
  requestAnimationFrame(() => inputRef.value?.focus?.())
})

async function dispatch(): Promise<void> {
  submitting.value = true
  error.value = null
  result.value = null
  try {
    result.value = await apiMutate<DispatchResult>('/api/v1/dispatch_tasks', {
      method: 'POST',
      body: { text: text.value },
      failureLabel: 'dispatch failed',
    })
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'dispatch failed'
  } finally {
    submitting.value = false
  }
}
</script>

<template>
  <NModal v-model:show="show" preset="card" title="Dispatch task" class="max-w-lg" data-testid="dispatch-task-modal">
    <p class="text-sm text-white/60 mt-0 mb-3">
      Fires the configured Claude Code routine in the cloud. Add optional context for this run, or dispatch with none.
      Or
      <RouterLink
        to="/agent"
        class="text-brand-primary hover:underline"
        data-testid="dispatch-agent-tab-link"
        @click="show = false"
        >start an ad-hoc session in the Agent tab</RouterLink
      >.
    </p>

    <NInput
      ref="inputRef"
      v-model:value="text"
      type="textarea"
      :rows="4"
      placeholder="Optional context for this run…"
      :disabled="submitting"
      data-testid="dispatch-input"
    />

    <NAlert v-if="error" type="error" class="mt-3" data-testid="dispatch-error">{{ error }}</NAlert>

    <div v-if="result" class="mt-3 flex flex-col gap-1" data-testid="dispatch-result">
      <NText class="text-sm">Session dispatched.</NText>
      <div class="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
        <!-- In-app link to the brain-side transcript (no new tab). Shown
             regardless of session_id — /agent lists the relayed run. -->
        <RouterLink
          to="/agent"
          class="text-brand-primary hover:underline"
          data-testid="dispatch-agent-link"
          @click="show = false"
          >View in Agent →</RouterLink
        >
        <!-- External link to the actual claude.ai run. -->
        <a
          :href="result.session_url"
          target="_blank"
          rel="noopener noreferrer"
          class="text-white/60 hover:underline"
          data-testid="dispatch-session-link"
          >monitor it on claude.ai →</a
        >
      </div>
    </div>

    <template #footer>
      <div class="flex justify-end">
        <NButton type="primary" :loading="submitting" data-testid="dispatch-submit" @click="dispatch">Dispatch</NButton>
      </div>
    </template>
  </NModal>
</template>
